/** @forge-header
 * @id          nhlParsers
 * @role        NHL ticker parsers | game moneyline
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:nhlTeamFromCode,parseFallback
 * @downstream  kalshiTicker/parse
 * @exports     parseNhlGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, nhlTeamFromCode } from '../helpers';

/** NHL game moneyline: KXNHLGAME-26FEB08SEATOR-SEA */
export function parseNhlGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  if (mid.length < 13) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);

  if (teamCodes.length < 6) {
    return parseFallback(raw);
  }

  const awayCode = teamCodes.slice(0, 3);
  const homeCode = teamCodes.slice(3, 6);
  const teamCode = parts[2] ?? null;

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: nhlTeamFromCode(awayCode) ?? undefined,
    homeName: nhlTeamFromCode(homeCode) ?? undefined,
  };

  const shortLabel = `NHL: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `NHL: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode} · ${teamCode} Win`
    : `NHL: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}`;

  return {
    raw,
    category: 'nhl-game',
    series: 'KXNHLGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}
